import Vue from 'vue'
import {Component} from 'vue-property-decorator'
import {ActivityStatus, CrudStatus} from '../../common/enums'
import {IPagination} from '../../models/generic'
import {BMEditedViewModel, IBMListingViewModel, IBMParameter} from '../../models/bm'
import {namespace} from 'vuex-class'
import {BM_ACTION, BM_CREATE_BM, BM_CREATE_MEETING, BM_MODULE_NAMESPACE, BM_PATCH_BM, BM_PATCH_MEETING, BM_POST_BM, BM_POST_MEETING} from '../../store/building-monitor/bm-types.store'
import {AUTHORIZATION_FUNCTIONALITIES, AUTHORIZATION_MODULE_NAMESPACE} from '../../store/authorization'
import {Uris} from '../../common/constants'
import {BMMeetingEditedViewModel} from '../../models/bm/bm-meeting-edited.viewmodel'
import {IBMMeetingDetailViewModel} from '../../models/bm/bm-meeting-detail.viewmodel'

const BMStore = namespace(BM_MODULE_NAMESPACE)
const AuthorizationStore = namespace(AUTHORIZATION_MODULE_NAMESPACE)

@Component({
  components: {
    'bm-modal-vertical': async () => import('./components/bm-modal-vertical/BMModalVertical.vue'),
    'bm-meeting-modal-vertical': async () => import('./components/bm-meeting-modal-vertical/BMMeetingModalVertical.vue')
  }
})
class BMContainer extends Vue {
  _clearTimeout: any;
  dtBMModalVisible: boolean;
  dtBMModalLoading: boolean;
  dtErrorModalVisible: boolean;
  dtError: any;

  @BMStore.Getter activityStatus: ActivityStatus;
  @BMStore.Getter pagination: IPagination;
  @BMStore.Getter bmParameter: IBMParameter;
  @BMStore.Getter bmsSource: { activityStatus: ActivityStatus, data: IBMListingViewModel[] };
  @BMStore.Getter bmCrudActionStatus: CrudStatus
  @BMStore.Getter bmEdited: BMEditedViewModel;
  @BMStore.Getter bmMeetingEdited: BMMeetingEditedViewModel;
  @BMStore.Getter bmMeeting: { activityStatus: ActivityStatus; data?: IBMMeetingDetailViewModel }

  @BMStore.Mutation(BM_ACTION)
  storeSetBMAction: (action: CrudStatus) => void

  @AuthorizationStore.Action(AUTHORIZATION_FUNCTIONALITIES)
  storeGetFunctionalities: (moduleName: string) => Promise<any>;
  @BMStore.Action(BM_CREATE_BM)
  storeCreateBM: () => void;
  @BMStore.Action(BM_CREATE_MEETING)
  storeCreateMeeting: () => void;
  @BMStore.Action(BM_POST_BM)
  storePostBM: () => Promise<any>;
  @BMStore.Action(BM_POST_MEETING)
  storePostMeeting: (payload: { bmid: string }) => Promise<any>;
  @BMStore.Action(BM_PATCH_BM)
  storePatchBM: (payload: { bmid: string }) => Promise<any>;
  @BMStore.Action(BM_PATCH_MEETING)
  storePatchMeeting: (payload: { bmid: string, idm: string }) => Promise<any>;

  get lblSetting () {
    return {
      ...require('@/locales/building-monitor').default
    }
  }

  constructor () {
    super()
    this.dtBMModalVisible = false
    this.dtBMModalLoading = false
    this.dtErrorModalVisible = false
    this.dtError = null
  }

  get modalTitle (): string {
    if (this.bmCrudActionStatus === CrudStatus.Create) {
      if (this.isBMModal) {
        return this.lblSetting.bmModalTitle
      } else if (this.isMeetingModal) {
        return this.lblSetting.bmMeetingModalTitle
      }
    } else if (this.bmCrudActionStatus === CrudStatus.Update) {
      if (this.isBMModal) {
        return this.lblSetting.bmUpdateModalTitle
      } else if (this.isMeetingModal) {
        return this.lblSetting.bmMeetingUpdateModalTitle
      }
    }
  }

  get modalBtnTitle (): string {
    if (this.bmCrudActionStatus === CrudStatus.Create) {
      if (this.isBMModal) {
        return this.lblSetting.bmModalBtnTitle
      } else if (this.isMeetingModal) {
        return this.lblSetting.bmMeetingModalBtnTitle
      }
    } else if (this.bmCrudActionStatus === CrudStatus.Update) {
      if (this.isBMModal) {
        return this.lblSetting.bmUpdateModalBtnTitle
      } else if (this.isMeetingModal) {
        return this.lblSetting.bmMeetingUpdateModalBtnTitle
      }
    }
  }

  get modalCreateOpen (): boolean {
    return this.bmCrudActionStatus === CrudStatus.Update ||
      this.bmCrudActionStatus === CrudStatus.Create
  }

  set modalCreateOpen (value: boolean) {
    if (!value) {
      this.storeSetBMAction(CrudStatus.NoAction)
    }
  }

  get isBMList (): boolean {
    return this.$route.path === Uris.BMBase
  }

  get isMeetingList (): boolean {
    return this.$route.path === Uris.BMMeetingBase
      .replace(':id', this.$route.params.id)
  }

  get isMeetingDetail (): boolean {
    return this.$route.params.idm && this.$route.path === Uris.BMMeetingById
      .replace(':id', this.$route.params.id)
      .replace(':idm', this.$route.params.idm)
  }

  get isCreateModal (): boolean {
    return this.bmCrudActionStatus === CrudStatus.Create
  }

  get isUpdateModal (): boolean {
    return this.bmCrudActionStatus === CrudStatus.Update
  }

  get isBMModal (): boolean {
    return (this.isBMList && this.isCreateModal) ||
      (this.isMeetingList && this.isUpdateModal)
  }

  get isMeetingModal (): boolean {
    return (this.isMeetingList && this.isCreateModal) ||
      (this.isMeetingDetail && this.isUpdateModal) ||
      (this.isMeetingDetail && this.isCreateModal)
  }


  handleSave (event) {
    this.dtBMModalLoading = true
    if (this.isBMModal) {
      this.handleSaveBM()
    } else if (this.isMeetingModal) {
      this.handleSaveMeeting()
    }
  }

  handleClose () {}

  handleBeforeModalClose () {}

  handleAddBM (functionality?: string) {
    if (this.isBMList) {
      this.storeCreateBM()
    } else if (this.isMeetingList || this.isMeetingDetail) {
      this.storeCreateMeeting()
    }
  }

  handleSaveBM () {
    if (this.bmEdited.fk_block) {
      if (this.bmCrudActionStatus === CrudStatus.Create) {
        this.storePostBM()
          .then(response => {
            this.modalCreateOpen = false
            this.handleRedirectToBM(response.id)
          })
          .catch(err => {
            this.dtErrorModalVisible = true
            this.dtError = err
          })
          .finally(() => this.dtBMModalLoading = false)
      } else if (this.bmCrudActionStatus === CrudStatus.Update) {
        this.storePatchBM({bmid: this.$route.params.id})
          .then(response => {
            this.modalCreateOpen = false
          })
          .catch(err => {
            this.dtErrorModalVisible = true
            this.dtError = err
          })
          .finally(() => this.dtBMModalLoading = false)
      }
    } else {
      this.dtBMModalLoading = false
    }
  }

  handleSaveMeeting () {
    if (this.bmMeetingEdited.date_start && this.bmMeetingEdited.date_end) {
      if (this.bmCrudActionStatus === CrudStatus.Create) {
        this.storePostMeeting({bmid: this.$route.params.id})
          .then(response => {
            this.modalCreateOpen = false
            this.handleRedirectToMeeting(response.id)
          })
          .catch(err => {
            this.dtErrorModalVisible = true
            this.dtError = err
          })
          .finally(() => this.dtBMModalLoading = false)
      } else if (this.bmCrudActionStatus === CrudStatus.Update) {
        let nextMeetingChange = (this.bmMeetingEdited.sendNextMeeting &&
          this.bmMeetingEdited.date_next_meeting !== this.bmMeeting.data.date_next_meeting) ||
          (!this.bmMeetingEdited.sendNextMeeting && this.bmMeeting.data.date_next_meeting)
        if (this.bmMeetingEdited.date_start === this.bmMeeting.data.date_start &&
          this.bmMeetingEdited.date_end === this.bmMeeting.data.date_end && !nextMeetingChange) {
          this.modalCreateOpen = false
          this.dtBMModalLoading = false
          return
        }
        this.storePatchMeeting({bmid: this.$route.params.id, idm: this.$route.params.idm})
          .then(response => {
            this.modalCreateOpen = false
          })
          .catch(err => {
            this.dtErrorModalVisible = true
            this.dtError = err
          })
          .finally(() => this.dtBMModalLoading = false)
      }
    } else {
      // data error
      this.dtError = {message: this.$t(this.lblSetting.bmDateMeetingRequired)}
      this.dtErrorModalVisible = true
    }
  }

  handleRemoveError () {
    this.dtErrorModalVisible = false
    this.dtError = null
  }

  handleRedirectToBM (bmid: number) {
    setTimeout(() => this.$router.push({
      path: `/bm/${bmid}`,
      params: {
        id: bmid.toString()
      }
    }), 100)
  }

  handleRedirectToMeeting (idm: number) {
    setTimeout(() => this.$router.push({
      path: `/bm/${this.$route.params.id}/meeting/${idm}`,
      params: {
        id: this.$route.params.id,
        idm: idm.toString()
      }
    }), 100)
  }
}

export default BMContainer



// WEBPACK FOOTER //
// ./src/containers/building-monitor/index.ts